import { Fragment, useCallback, useEffect, useMemo, useRef, useState, MouseEvent } from "react";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { DateRange } from "@mui/x-date-pickers-pro";
import {
    Alert,
    Box,
    Card,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useTheme } from "@mui/material/styles";
import axiosInstance from "../AxiosInstance";
import { downloadCsv } from "../Utilities/csvUtility";
import { getSavedEposRetailer, saveEposRetailer } from "../Utilities/eposRetailerPreference";
import CustomDatePicker from "./CustomDatePicker";
import EposPerformanceTrendChart from "./EposPerformanceTrendChart";
import EposStoreHeatmap from "./EposStoreHeatmap";
import EposWaitroseDataView from "./EposWaitroseDataView";
import EposTabs from "./EposTabs";
import ProductFilters from "./ProductFilters";
import ProductFiltersButton from "./ProductFiltersButton";

type Grain = "weekly" | "daily";
type ChartMode = "total" | "sku";

interface EposPerformanceWeek {
    weekEndingDate: string;
    units: number;
    revenue: number;
    storesSelling: number;
    storeStock: number;
    ros: number | null;
    poundRos: number | null;
    asp: number | null;
}

interface EposPerformanceDay {
    recordDate: string;
    units: number;
    revenue: number;
    salesCases: number;
    storesRanged: number;
    storeStock: number;
    asp: number | null;
}

interface EposWeeklyPerformanceResponse {
    weeks: EposPerformanceWeek[];
}

interface EposDailyPerformanceResponse {
    days: EposPerformanceDay[];
}

interface EposPerformanceSkuValue {
    period: string;
    value: number | null;
}

interface EposPerformanceSku {
    productId: number;
    productName: string;
    values: EposPerformanceSkuValue[];
}

interface EposPerformanceSkuDetailsResponse {
    items: EposPerformanceSku[];
    isTruncated?: boolean;
    resultLimit?: number;
}

interface PerformancePoint {
    date: string;
    units: number;
    revenue: number;
    salesCases: number | null;
    storesSelling: number | null;
    storesRanged: number | null;
    storeStock: number;
    ros: number | null;
    poundRos: number | null;
    asp: number | null;
}

interface ProductOption {
    productId: number;
    skuNumber: string;
    skuDescription: string;
    brand: string;
    groupName?: string | null;
}

interface ProductFilterParams {
    productIds?: number[];
    brands?: string[];
    productGroups?: string[];
}

interface CurrentOrganisation {
    startDayOfWeek: number;
}

type StoreSortKey =
    | "storeNumber"
    | "storeName"
    | "grossSalesAmount"
    | "grossSalesAfterProductDiscountAmount"
    | "salesAmount"
    | "salesCases"
    | "salesUnits"
    | "salesAmountApportioned"
    | "salesUnitsApportioned"
    | "storeOnHandUnitsEnding";

interface EposStorePerformance {
    eposStoreId: string;
    storeNumber: string;
    storeName: string;
    grossSalesAmount: number | null;
    grossSalesAfterProductDiscountAmount: number | null;
    salesAmount: number | null;
    salesCases: number | null;
    salesUnits: number | null;
    salesAmountApportioned: number | null;
    salesUnitsApportioned: number | null;
    storeOnHandUnitsEnding: number | null;
}

interface EposStorePerformanceResponse {
    items: EposStorePerformance[];
    totalCount: number;
    page: number;
    pageSize: number;
    isAvailable: boolean;
}

interface StoreColumnDefinition {
    key: StoreSortKey;
    label: string;
    align?: "left" | "right";
    format?: (value: number | null) => string;
}

const storeColumns: StoreColumnDefinition[] = [
    { key: "storeNumber", label: "Store number" },
    { key: "storeName", label: "Store name" },
    { key: "grossSalesAmount", label: "Gross sales", align: "right", format: value => formatCurrency(value, 2) },
    { key: "grossSalesAfterProductDiscountAmount", label: "Gross sales after discount", align: "right", format: value => formatCurrency(value, 2) },
    { key: "salesAmount", label: "Sales amount", align: "right", format: value => formatCurrency(value, 2) },
    { key: "salesCases", label: "Sales cases", align: "right", format: value => formatNumber(value, 2) },
    { key: "salesUnits", label: "Sales units", align: "right", format: value => formatNumber(value, 2) },
    { key: "salesAmountApportioned", label: "Sales amount apportioned", align: "right", format: value => formatCurrency(value, 2) },
    { key: "salesUnitsApportioned", label: "Sales units apportioned", align: "right", format: value => formatNumber(value, 2) },
    { key: "storeOnHandUnitsEnding", label: "Store on hand units ending", align: "right", format: value => formatNumber(value, 2) }
];

type PerformanceMetricKey =
    | "units"
    | "revenue"
    | "salesCases"
    | "ros"
    | "poundRos"
    | "asp"
    | "storesSelling"
    | "storesRanged"
    | "storeStock";
type MetricKey = PerformanceMetricKey | "none";

interface MetricDefinition {
    key: PerformanceMetricKey;
    label: string;
}

const weeklyMetricDefinitions: MetricDefinition[] = [
    { key: "units", label: "Units" },
    { key: "revenue", label: "Revenue" },
    { key: "ros", label: "ROS" },
    { key: "poundRos", label: "£ ROS" },
    { key: "asp", label: "ASP" },
    { key: "storesSelling", label: "Stores selling" },
    { key: "storeStock", label: "Store stock" }
];

const dailyMetricDefinitions: MetricDefinition[] = [
    { key: "units", label: "Units" },
    { key: "revenue", label: "Revenue" },
    { key: "salesCases", label: "Sales cases" },
    { key: "asp", label: "ASP" },
    { key: "storesRanged", label: "Stores ranged" },
    { key: "storeStock", label: "Store stock" }
];

const lightSkuChartColors = [
    "#1565C0",
    "#2E7D32",
    "#C62828",
    "#6A1B9A",
    "#E65100",
    "#00838F",
    "#AD1457",
    "#4527A0",
    "#558B2F",
    "#0277BD",
    "#7B1FA2",
    "#00695C",
    "#8E24AA",
    "#BF360C",
    "#37474F"
];

const darkSkuChartColors = [
    "#90CAF9",
    "#81C784",
    "#EF9A9A",
    "#CE93D8",
    "#FFB74D",
    "#80DEEA",
    "#F48FB1",
    "#B39DDB",
    "#AED581",
    "#81D4FA",
    "#BA68C8",
    "#80CBC4",
    "#D1C4E9",
    "#FFAB91",
    "#B0BEC5"
];

const formatNumber = (value: number | null, maximumFractionDigits = 0) =>
    value === null
        ? "—"
        : new Intl.NumberFormat("en-GB", { maximumFractionDigits }).format(value);

const formatCurrency = (value: number | null, maximumFractionDigits = 0) =>
    value === null
        ? "—"
        : new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
            maximumFractionDigits
        }).format(value);

const formatPeriod = (date: string) => dayjs(date).format("D MMM");

const metricValue = (point: PerformancePoint, key: MetricKey) => key === "none" ? null : point[key];

const formatMetricValueValue = (value: number | null, key: PerformanceMetricKey) => {

    if (key === "revenue" || key === "poundRos" || key === "asp") {
        return formatCurrency(value, key === "revenue" ? 0 : 2);
    }

    if (key === "salesCases") {
        return formatNumber(value, 2);
    }

    return formatNumber(value, key === "ros" ? 1 : 0);
};

const formatMetricValue = (point: PerformancePoint, key: PerformanceMetricKey) =>
    formatMetricValueValue(metricValue(point, key), key);

const errorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string; detail?: string } | undefined;
        return data?.message ?? data?.detail ?? "EPOS performance could not be loaded.";
    }

    return "EPOS performance could not be loaded.";
};

const sum = (points: PerformancePoint[], key: "units" | "revenue" | "salesCases") =>
    points.reduce((total, point) => total + (point[key] ?? 0), 0);

const notNull = <T,>(value: T | null): value is T => value !== null;

const getDefaultWeeklyDateRange = (startDayOfWeek: number): DateRange<Dayjs> => {
    const normalizedStartDayOfWeek = startDayOfWeek === 7 ? 0 : startDayOfWeek;
    const currentDate = dayjs().startOf("day");
    const currentWeekStart = currentDate.subtract((currentDate.day() - normalizedStartDayOfWeek + 7) % 7, "day");

    return [
        currentWeekStart.subtract(52, "week"),
        currentWeekStart.subtract(1, "day")
    ];
};

const getDefaultDailyDateRange = (): DateRange<Dayjs> => {
    const currentDate = dayjs().startOf("day");
    return [currentDate.subtract(30, "day"), currentDate];
};

const areDateRangesEqual = (first: DateRange<Dayjs>, second: DateRange<Dayjs>) =>
    (first[0] === null ? second[0] === null : second[0] !== null && first[0].isSame(second[0], "day")) &&
    (first[1] === null ? second[1] === null : second[1] !== null && first[1].isSame(second[1], "day"));

const toWeeklyPoints = (weeks: EposPerformanceWeek[]): PerformancePoint[] =>
    weeks.map(week => ({
        date: week.weekEndingDate,
        units: week.units,
        revenue: week.revenue,
        salesCases: null,
        storesSelling: week.storesSelling,
        storesRanged: null,
        storeStock: week.storeStock,
        ros: week.ros,
        poundRos: week.poundRos,
        asp: week.asp
    }));

const toDailyPoints = (days: EposPerformanceDay[]): PerformancePoint[] =>
    days.map(day => ({
        date: day.recordDate,
        units: day.units,
        revenue: day.revenue,
        salesCases: day.salesCases,
        storesSelling: null,
        storesRanged: day.storesRanged,
        storeStock: day.storeStock,
        ros: null,
        poundRos: null,
        asp: day.asp
    }));

const DATE_FILTER_DEBOUNCE_MS = 500;

const sanitizeSpreadsheetText = (value: string) =>
    /^[\t\r\n ]*[=+\-@]/.test(value) ? `'${value}` : value;

const formatStoreCell = (store: EposStorePerformance, column: StoreColumnDefinition) => {
    const value = store[column.key];
    return column.format
        ? column.format(value as number | null)
        : sanitizeSpreadsheetText(String(value ?? ""));
};

const buildStoreTableMatrix = (stores: EposStorePerformance[]) => ({
    headers: storeColumns.map(column => column.label),
    rows: stores.map(store => storeColumns.map(column => formatStoreCell(store, column)))
});

const toTabSeparatedValue = (headers: string[], rows: string[][]) =>
    [headers, ...rows]
        .map(row => row.map(value => String(value).replace(/[\t\r\n]/g, " ")).join("\t"))
        .join("\n");

const toCsvContent = (headers: string[], rows: string[][]) =>
    [headers, ...rows]
        .map(row => row.map(value => '"' + String(value).replace(/"/g, '""') + '"').join(","))
        .join("\n");

const downloadCsvContent = (csv: string, fileName: string) => {
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

export default function EposDataPage() {
    const theme = useTheme();
    const [grain, setGrain] = useState<Grain>("weekly");
    const [startDayOfWeek, setStartDayOfWeek] = useState(0);
    const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([null, null]);
    const [queryDateRange, setQueryDateRange] = useState<DateRange<Dayjs>>([null, null]);
    const [selectedRetailer, setSelectedRetailer] = useState(getSavedEposRetailer);
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
    const [selectedProductGroups, setSelectedProductGroups] = useState<string[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
    const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
    const productFilterParams = useMemo<ProductFilterParams>(() => ({
        ...(selectedProductIds.length > 0 ? { productIds: selectedProductIds } : {}),
        ...(selectedBrands.length > 0 ? { brands: selectedBrands } : {}),
        ...(selectedProductGroups.length > 0 ? { productGroups: selectedProductGroups } : {})
    }), [selectedBrands, selectedProductGroups, selectedProductIds]);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [points, setPoints] = useState<PerformancePoint[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [chartMode, setChartMode] = useState<ChartMode>("total");
    const [metricOne, setMetricOne] = useState<MetricKey>("units");
    const [metricTwo, setMetricTwo] = useState<MetricKey>("revenue");
    const [unmappedProductCount, setUnmappedProductCount] = useState(0);
    const [expandedMetrics, setExpandedMetrics] = useState<Set<PerformanceMetricKey>>(new Set());
    const [metricDetails, setMetricDetails] = useState<Partial<Record<PerformanceMetricKey, EposPerformanceSkuDetailsResponse>>>({});
    const [metricDetailLoading, setMetricDetailLoading] = useState<Partial<Record<PerformanceMetricKey, boolean>>>({});
    const [metricDetailErrors, setMetricDetailErrors] = useState<Partial<Record<PerformanceMetricKey, string>>>({});
    const [storePerformance, setStorePerformance] = useState<EposStorePerformance[]>([]);
    const [storePerformanceTotal, setStorePerformanceTotal] = useState(0);
    const [storePerformanceAvailable, setStorePerformanceAvailable] = useState(false);
    const [storePerformanceLoading, setStorePerformanceLoading] = useState(true);
    const [storePerformanceError, setStorePerformanceError] = useState("");
    const [storeActionError, setStoreActionError] = useState("");
    const [storeCsvExportLoading, setStoreCsvExportLoading] = useState(false);
    const [storePage, setStorePage] = useState(0);
    const [storePageSize, setStorePageSize] = useState(10);
    const [storeSortBy, setStoreSortBy] = useState<StoreSortKey>("salesAmount");
    const [storeSortDirection, setStoreSortDirection] = useState<"asc" | "desc">("desc");
    const performanceRequestController = useRef<AbortController | null>(null);
    const storePerformanceRequestController = useRef<AbortController | null>(null);
    const storeCsvExportRequestController = useRef<AbortController | null>(null);
    const metricDetailRequestControllers = useRef(new Map<PerformanceMetricKey, AbortController>());
    const metricTwoBeforeSkuMode = useRef<MetricKey>("revenue");
    const dateFilterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelStoreCsvExport = useCallback(() => {
        const controller = storeCsvExportRequestController.current;
        if (!controller) {
            return;
        }

        controller.abort();
        storeCsvExportRequestController.current = null;
        setStoreCsvExportLoading(false);
    }, []);

    const clearDateFilterDebounce = () => {
        if (dateFilterDebounceRef.current !== null) {
            clearTimeout(dateFilterDebounceRef.current);
            dateFilterDebounceRef.current = null;
        }
    };

    const commitDateRange = (nextRange: DateRange<Dayjs>) => {
        clearDateFilterDebounce();
        cancelStoreCsvExport();
        setDateRange(nextRange);
        setQueryDateRange(nextRange);
    };

    const handleDateRangeChange = (nextRange: DateRange<Dayjs>) => {
        cancelStoreCsvExport();
        setDateRange(nextRange);
        clearDateFilterDebounce();
        dateFilterDebounceRef.current = setTimeout(() => {
            dateFilterDebounceRef.current = null;
            setQueryDateRange(nextRange);
        }, DATE_FILTER_DEBOUNCE_MS);
    };

    const metricDefinitions = grain === "weekly" ? weeklyMetricDefinitions : dailyMetricDefinitions;
    const isDateRangeQueryCurrent = dateRange[0] !== null &&
        dateRange[1] !== null &&
        areDateRangesEqual(dateRange, queryDateRange);

    useEffect(() => {
        if (selectedRetailer === "3") {
            setUnmappedProductCount(0);
            return;
        }

        let cancelled = false;

        const loadUnmappedProductCount = async () => {
            try {
                const response = await axiosInstance.get<{ count: number }>("/epos/product-mappings/unmapped-count", {
                    params: { retailer: selectedRetailer }
                });

                if (!cancelled) {
                    setUnmappedProductCount(response.data.count);
                }
            } catch {
                if (!cancelled) {
                    setUnmappedProductCount(0);
                }
            }
        };

        void loadUnmappedProductCount();

        return () => {
            cancelled = true;
        };
    }, [selectedRetailer]);

    useEffect(() => {
        const loadOrganisation = async () => {
            try {
                const { data } = await axiosInstance.get<CurrentOrganisation>("/organisation/current");
                const nextRange = getDefaultWeeklyDateRange(data.startDayOfWeek);
                setStartDayOfWeek(data.startDayOfWeek);
                setDateRange(currentDateRange =>
                    currentDateRange[0] && currentDateRange[1]
                        ? currentDateRange
                        : nextRange
                );
                setQueryDateRange(currentDateRange =>
                    currentDateRange[0] && currentDateRange[1]
                        ? currentDateRange
                        : nextRange
                );
            } catch {
                const nextRange = getDefaultWeeklyDateRange(0);
                setDateRange(currentDateRange =>
                    currentDateRange[0] && currentDateRange[1]
                        ? currentDateRange
                        : nextRange
                );
                setQueryDateRange(currentDateRange =>
                    currentDateRange[0] && currentDateRange[1]
                        ? currentDateRange
                        : nextRange
                );
            }
        };

        void loadOrganisation();

        return () => clearDateFilterDebounce();
    }, []);

    const hasProductFilters = selectedBrands.length > 0 || selectedProductGroups.length > 0 || selectedProductIds.length > 0;

    const hasNoMatchingProducts = useMemo(() => hasProductFilters && !productOptions.some(product =>
        (selectedBrands.length === 0 || selectedBrands.includes(product.brand)) &&
        (selectedProductGroups.length === 0 || selectedProductGroups.includes(product.groupName ?? "")) &&
        (selectedProductIds.length === 0 || selectedProductIds.includes(product.productId))),
    [hasProductFilters, productOptions, selectedBrands, selectedProductGroups, selectedProductIds]);

    const resetMetricDetails = useCallback(() => {
        metricDetailRequestControllers.current.forEach(controller => controller.abort());
        metricDetailRequestControllers.current.clear();
        setExpandedMetrics(new Set());
        setMetricDetails({});
        setMetricDetailLoading({});
        setMetricDetailErrors({});
    }, []);

    const load = useCallback(async () => {
        if (selectedRetailer === "3") {
            resetMetricDetails();
            setPoints([]);
            setLoading(false);
            setError("");
            return;
        }

        const fromDate = queryDateRange[0];
        const toDate = queryDateRange[1];

        if (!fromDate || !toDate) {
            return;
        }

        performanceRequestController.current?.abort();
        const controller = new AbortController();
        performanceRequestController.current = controller;
        resetMetricDetails();

        if (hasNoMatchingProducts) {
            if (!controller.signal.aborted) {
                setPoints([]);
                setLoading(false);
            }
            return;
        }

        setLoading(true);
        setError("");
        setPoints([]);

        try {
            const params = {
                retailer: selectedRetailer,
                fromDate: fromDate.format("YYYY-MM-DD"),
                toDate: toDate.format("YYYY-MM-DD"),
                ...productFilterParams
            };

            if (grain === "daily") {
                const response = await axiosInstance.get<EposDailyPerformanceResponse>("/epos/performance/daily", {
                    params,
                    paramsSerializer: { indexes: null },
                    signal: controller.signal
                });
                if (!controller.signal.aborted) {
                    setPoints(toDailyPoints(response.data.days));
                }
            } else {
                const response = await axiosInstance.get<EposWeeklyPerformanceResponse>("/epos/performance", {
                    params,
                    paramsSerializer: { indexes: null },
                    signal: controller.signal
                });
                if (!controller.signal.aborted) {
                    setPoints(toWeeklyPoints(response.data.weeks));
                }
            }
        } catch (requestError) {
            if (!controller.signal.aborted) {
                setPoints([]);
                setError(errorMessage(requestError));
            }
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [grain, hasNoMatchingProducts, productFilterParams, queryDateRange, resetMetricDetails, selectedRetailer]);

    useEffect(() => {
        const detailRequestControllers = metricDetailRequestControllers.current;
        void load();

        return () => {
            performanceRequestController.current?.abort();
            detailRequestControllers.forEach(controller => controller.abort());
        };
    }, [load]);

    const loadStorePerformance = useCallback(async () => {
        if (selectedRetailer === "3") {
            setStorePerformance([]);
            setStorePerformanceTotal(0);
            setStorePerformanceAvailable(false);
            setStorePerformanceLoading(false);
            setStorePerformanceError("");
            return;
        }

        const fromDate = queryDateRange[0];
        const toDate = queryDateRange[1];

        if (!fromDate || !toDate) {
            return;
        }

        storePerformanceRequestController.current?.abort();
        const controller = new AbortController();
        storePerformanceRequestController.current = controller;
        setStorePerformanceLoading(true);
        setStorePerformanceError("");
        setStoreActionError("");

        if (hasNoMatchingProducts) {
            setStorePerformance([]);
            setStorePerformanceTotal(0);
            setStorePerformanceAvailable(true);
            setStorePerformanceLoading(false);
            return;
        }

        try {
            const response = await axiosInstance.get<EposStorePerformanceResponse>("/epos/performance/stores", {
                params: {
                    retailer: selectedRetailer,
                    fromDate: fromDate.format("YYYY-MM-DD"),
                    toDate: toDate.format("YYYY-MM-DD"),
                    grain,
                    ...productFilterParams,
                    sortBy: storeSortBy,
                    sortDirection: storeSortDirection,
                    page: storePage + 1,
                    pageSize: storePageSize
                },
                paramsSerializer: { indexes: null },
                signal: controller.signal
            });

            if (!controller.signal.aborted) {
                setStorePerformance(response.data.items);
                setStorePerformanceTotal(response.data.totalCount);
                setStorePerformanceAvailable(response.data.isAvailable);
            }
        } catch (requestError) {
            if (!controller.signal.aborted) {
                setStorePerformance([]);
                setStorePerformanceTotal(0);
                if (axios.isAxiosError(requestError)) {
                    const data = requestError.response?.data as { message?: string; detail?: string } | undefined;
                    setStorePerformanceError(data?.message ?? data?.detail ?? "Store-level EPOS performance could not be loaded.");
                } else {
                    setStorePerformanceError("Store-level EPOS performance could not be loaded.");
                }
            }
        } finally {
            if (!controller.signal.aborted) {
                setStorePerformanceLoading(false);
            }
        }
    }, [grain, hasNoMatchingProducts, productFilterParams, queryDateRange, selectedRetailer, storePage, storePageSize, storeSortBy, storeSortDirection]);

    useEffect(() => {
        void loadStorePerformance();
        return () => {
            storePerformanceRequestController.current?.abort();
            cancelStoreCsvExport();
        };
    }, [cancelStoreCsvExport, loadStorePerformance]);

    useEffect(() => {
        setStorePage(0);
    }, [grain, hasNoMatchingProducts, productFilterParams, queryDateRange, selectedRetailer]);

    const latestPoint = points.length > 0 ? points[points.length - 1] : null;

    const summary = useMemo(() => {
        const units = sum(points, "units");
        const revenue = sum(points, "revenue");
        const salesCases = sum(points, "salesCases");
        const storesSelling = points.reduce((total, point) => total + (point.storesSelling ?? 0), 0);

        return {
            units,
            revenue,
            salesCases,
            ros: storesSelling === 0 || points.some(point => point.units !== 0 && point.ros === null)
                ? null : units / storesSelling,
            poundRos: storesSelling === 0 || points.some(point => point.revenue !== 0 && point.poundRos === null)
                ? null : revenue / storesSelling,
            asp: units === 0 ? null : revenue / units
        };
    }, [points]);

    const performanceTable = useMemo(() => {
        const headers = ["Metric", ...points.map(point => formatPeriod(point.date))];
        const rows = metricDefinitions.map(metric => ([
            metric.label,
            ...points.map(point => formatMetricValue(point, metric.key))
        ]));

        return { headers, rows };
    }, [metricDefinitions, points]);

    const loadMetricDetails = useCallback(async (metric: PerformanceMetricKey, retryOnError = false) => {
        if (selectedRetailer === "3") {
            return;
        }

        const fromDate = queryDateRange[0];
        const toDate = queryDateRange[1];

        if (!fromDate ||
            !toDate ||
            metricDetails[metric] ||
            metricDetailLoading[metric] ||
            (!retryOnError && metricDetailErrors[metric])) {
            return;
        }

        metricDetailRequestControllers.current.get(metric)?.abort();
        const controller = new AbortController();
        metricDetailRequestControllers.current.set(metric, controller);
        setMetricDetailLoading(current => ({ ...current, [metric]: true }));
        setMetricDetailErrors(current => ({ ...current, [metric]: undefined }));

        const params = {
            retailer: selectedRetailer,
            fromDate: fromDate.format("YYYY-MM-DD"),
            toDate: toDate.format("YYYY-MM-DD"),
            metric,
            ...productFilterParams
        };

        try {
            const url = grain === "daily"
                ? "/epos/performance/daily/details"
                : "/epos/performance/details";
            const response = await axiosInstance.get<EposPerformanceSkuDetailsResponse>(url, {
                params,
                paramsSerializer: { indexes: null },
                signal: controller.signal
            });

            if (!controller.signal.aborted) {
                setMetricDetails(current => ({ ...current, [metric]: response.data }));
            }
        } catch (requestError) {
            if (!controller.signal.aborted) {
                setMetricDetailErrors(current => ({ ...current, [metric]: errorMessage(requestError) }));
            }
        } finally {
            if (!controller.signal.aborted) {
                setMetricDetailLoading(current => ({ ...current, [metric]: false }));
                metricDetailRequestControllers.current.delete(metric);
            }
        }
    }, [grain, metricDetailErrors, metricDetailLoading, metricDetails, productFilterParams, queryDateRange, selectedRetailer]);

    useEffect(() => {
        if (chartMode !== "sku" || metricOne === "none") {
            return;
        }

        void loadMetricDetails(metricOne);
    }, [chartMode, loadMetricDetails, metricOne]);

    const toggleMetric = (metric: PerformanceMetricKey) => {
        const isExpanded = expandedMetrics.has(metric);
        setExpandedMetrics(current => {
            const next = new Set(current);
            if (isExpanded) {
                next.delete(metric);
            } else {
                next.add(metric);
            }
            return next;
        });

        if (!isExpanded) {
            void loadMetricDetails(metric, true);
        }
    };

    const chartSeries = chartMode === "sku"
        ? metricOne === "none"
            ? []
            : (metricDetails[metricOne]?.items ?? []).map(item => ({
                label: item.productName,
                data: points.map(point =>
                    item.values.find(value => value.period === point.date)?.value ?? null),
                yAxisId: "metric-one"
            }))
        : [
            metricOne === "none" ? null : {
                label: metricDefinitions.find(metric => metric.key === metricOne)?.label ?? metricOne,
                data: points.map(point => metricValue(point, metricOne)),
                yAxisId: "metric-one"
            },
            metricTwo === "none" ? null : {
                label: metricDefinitions.find(metric => metric.key === metricTwo)?.label ?? metricTwo,
                data: points.map(point => metricValue(point, metricTwo)),
                yAxisId: "metric-two"
            }
        ].filter(notNull);

    const chartYAxes = chartMode === "total" && metricTwo !== "none"
        ? [
            { id: "metric-one", scaleType: "linear" as const, position: "left" as const },
            { id: "metric-two", scaleType: "linear" as const, position: "right" as const }
        ]
        : [{ id: "metric-one", scaleType: "linear" as const, position: "left" as const }];

    const selectedSkuMetric = metricOne === "none" ? null : metricOne;
    const skuDetails = selectedSkuMetric ? metricDetails[selectedSkuMetric] : undefined;
    const skuDetailError = chartMode === "sku" && selectedSkuMetric
        ? metricDetailErrors[selectedSkuMetric]
        : undefined;
    const skuChartLoading = chartMode === "sku" && selectedSkuMetric !== null && !skuDetails && !skuDetailError;
    const skuChartColors = theme.palette.mode === "dark" ? darkSkuChartColors : lightSkuChartColors;
    const totalChartColors = [theme.palette.primary.main, theme.palette.secondary.main];
    const chartColors = chartSeries.map((_, index) => chartMode === "sku"
        ? skuChartColors[index % skuChartColors.length]
        : totalChartColors[index % totalChartColors.length]);

    const handleChartModeChange = (_event: MouseEvent<HTMLElement>, nextMode: ChartMode | null) => {
        if (!nextMode || nextMode === chartMode) {
            return;
        }

        if (nextMode === "sku") {
            metricTwoBeforeSkuMode.current = metricTwo;
            setMetricTwo("none");
            if (metricOne !== "none") {
                void loadMetricDetails(metricOne, true);
            }
        } else {
            const savedMetricTwo = metricTwoBeforeSkuMode.current;
            const metricTwoIsAvailable = savedMetricTwo === "none" ||
                metricDefinitions.some(metric => metric.key === savedMetricTwo);
            const restoredMetricTwo = metricTwoIsAvailable ? savedMetricTwo : "revenue";

            metricTwoBeforeSkuMode.current = restoredMetricTwo;
            setMetricTwo(restoredMetricTwo);
        }

        setChartMode(nextMode);
    };

    const handleGrainChange = (_event: MouseEvent<HTMLElement>, nextGrain: Grain | null) => {
        if (!nextGrain || nextGrain === grain) {
            return;
        }

        setGrain(nextGrain);
        setMetricOne("units");
        if (chartMode === "sku") {
            setMetricTwo("none");
        } else {
            setMetricTwo("revenue");
            metricTwoBeforeSkuMode.current = "revenue";
        }
        setPoints([]);
        setError("");
        commitDateRange(nextGrain === "daily"
            ? getDefaultDailyDateRange()
            : getDefaultWeeklyDateRange(startDayOfWeek));
    };

    const handleStoreSort = (key: StoreSortKey) => {
        setStorePage(0);
        if (storeSortBy === key) {
            setStoreSortDirection(direction => direction === "asc" ? "desc" : "asc");
        } else {
            setStoreSortBy(key);
            setStoreSortDirection("desc");
        }
    };

    const exportCsv = () => {
        if (points.length === 0 || !isDateRangeQueryCurrent) {
            return;
        }

        downloadCsvContent(
            toCsvContent(performanceTable.headers, performanceTable.rows),
            grain === "daily" ? "epos-performance-daily.csv" : "epos-performance.csv"
        );
    };

    const copyPerformanceToClipboard = async () => {
        if (points.length === 0 || !isDateRangeQueryCurrent) {
            return;
        }

        try {
            await navigator.clipboard.writeText(toTabSeparatedValue(performanceTable.headers, performanceTable.rows));
        } catch (clipboardError) {
            console.error("Failed to copy performance to clipboard:", clipboardError);
        }
    };

    const copyStorePerformanceToClipboard = async () => {
        if (storePerformance.length === 0 || !isDateRangeQueryCurrent) {
            return;
        }

        const { headers, rows } = buildStoreTableMatrix(storePerformance);

        try {
            await navigator.clipboard.writeText(toTabSeparatedValue(headers, rows));
            setStoreActionError("");
        } catch (clipboardError) {
            console.error("Failed to copy store performance to clipboard:", clipboardError);
            setStoreActionError("Store performance could not be copied to the clipboard.");
        }
    };

    const exportStoreCsv = async () => {
        if (storePerformance.length === 0 || !isDateRangeQueryCurrent || storeCsvExportLoading) {
            return;
        }

        const fromDate = queryDateRange[0];
        const toDate = queryDateRange[1];
        if (!fromDate || !toDate) {
            return;
        }

        storeCsvExportRequestController.current?.abort();
        const controller = new AbortController();
        storeCsvExportRequestController.current = controller;
        const isCurrentExport = () => storeCsvExportRequestController.current === controller && !controller.signal.aborted;
        setStoreActionError("");
        await downloadCsv(
            "/epos/performance/stores/csv",
            {
                retailer: selectedRetailer,
                fromDate: fromDate.format("YYYY-MM-DD"),
                toDate: toDate.format("YYYY-MM-DD"),
                grain,
                ...productFilterParams,
                sortBy: storeSortBy,
                sortDirection: storeSortDirection
            },
            grain === "daily" ? "epos-store-performance-daily.csv" : "epos-store-performance.csv",
            loading => {
                if (isCurrentExport()) {
                    setStoreCsvExportLoading(loading);
                }
            },
            () => {
                if (isCurrentExport()) {
                    setStoreActionError("Store performance could not be exported. Please try again.");
                }
            },
            { paramsSerializer: { indexes: null }, signal: controller.signal }
        );
        if (storeCsvExportRequestController.current === controller) {
            storeCsvExportRequestController.current = null;
        }
    };

    const renderMetricSelect = (
        label: string,
        value: MetricKey,
        onChange: (nextValue: MetricKey) => void,
        allowNone = false,
        disabled = false
    ) => {
        const labelId = `${label.toLowerCase().replace(/\s+/g, "-")}-label`;

        return (
            <FormControl size="small" sx={{ minWidth: 142 }} disabled={disabled}>
                <InputLabel id={labelId}>{label}</InputLabel>
                <Select
                    labelId={labelId}
                    label={label}
                    value={value}
                    disabled={disabled}
                    onChange={event => onChange(event.target.value as MetricKey)}
                >
                    {(allowNone ? [{ key: "none" as const, label: "None" }, ...metricDefinitions] : metricDefinitions).map(metric => (
                        <MenuItem key={metric.key} value={metric.key}>{metric.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const handleRetailerChange = (retailer: string) => {
        setSelectedRetailer(saveEposRetailer(retailer));
        setSelectedBrands([]);
        setSelectedProductGroups([]);
        setSelectedProductIds([]);
        setProductOptions([]);
    };

    if (selectedRetailer === "3") {
        return <EposWaitroseDataView onSelectRetailer={handleRetailerChange} startDayOfWeek={startDayOfWeek} />;
    }

    return (
        <Box sx={{ width: "100%", maxWidth: 1500, minWidth: 0, mx: "auto", py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
            <EposTabs
                unmappedCount={unmappedProductCount}
                retailer={selectedRetailer}
                onRetailerChange={handleRetailerChange}
            />

            {unmappedProductCount > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 3 }}
                >
                    {unmappedProductCount === 1
                        ? "Some EPOS performance features may be unavailable until 1 unlinked product is resolved."
                        : `Some EPOS performance features may be unavailable until ${unmappedProductCount} unlinked products are resolved.`}
                </Alert>
            )}

            <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-end"
                    sx={{
                        "& .MuiStack-root": {
                            overflow: "visible",
                            paddingTop: 0
                        }
                    }}
                >
                    <Grid item xs={12} sm="auto" sx={{ display: "flex", alignItems: "center", alignSelf: "stretch" }}>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={grain}
                            onChange={handleGrainChange}
                            aria-label="Performance period"
                        >
                            <ToggleButton value="weekly">Weekly</ToggleButton>
                            <ToggleButton value="daily">Daily</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={12} lg={5} sx={{ display: "flex", alignItems: "flex-end" }}>
                        <Box sx={{ width: "100%" }}>
                            <CustomDatePicker
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                startLabel="From"
                                endLabel="To"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm="auto" sx={{ display: "flex", alignItems: "center", alignSelf: "stretch" }}>
                        <ProductFiltersButton
                            filtersExpanded={filtersExpanded}
                            onToggle={() => setFiltersExpanded(expanded => !expanded)}
                        />
                    </Grid>
                </Grid>
            </Card>

            <ProductFilters
                selectedForecastId={null}
                selectedForecastPeriodId={null}
                selectedBrands={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                selectedProductGroups={selectedProductGroups}
                setSelectedProductGroups={setSelectedProductGroups}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                filtersExpanded={filtersExpanded}
                onToggle={() => setFiltersExpanded(expanded => !expanded)}
                selectedCustomerId={null}
                selectedChannelId={null}
                productsUrl={`/epos/mapped-products?retailer=${selectedRetailer}`}
                onProductsLoaded={setProductOptions}
            />

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {loading ? (
                <Card variant="outlined" sx={{ width: "100%", minWidth: { xs: 0, sm: 600 }, mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                        <CircularProgress />
                    </Box>
                </Card>
            ) : points.length === 0 ? (
                <Card variant="outlined" sx={{ width: "100%", minWidth: { xs: 0, sm: 600 }, mb: 3 }}>
                    <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                        <Typography variant="h6">No data</Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            {unmappedProductCount > 0
                                ? "Check filter criteria and product links."
                                : "Check filter criteria."}
                        </Typography>
                    </Box>
                </Card>
            ) : (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard label="Units" value={formatNumber(summary.units)} detail="Selected period" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard
                                label="Revenue"
                                value={formatCurrency(summary.revenue)}
                                detail={grain === "daily" ? "Apportioned, selected period" : "Selected period"}
                            />
                        </Grid>
                        {grain === "weekly" ? (
                            <>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MetricCard label="ROS" value={formatNumber(summary.ros, 1)} detail="Units/store/week" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MetricCard label="£ ROS" value={formatCurrency(summary.poundRos, 2)} detail="Per store/week" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MetricCard label="ASP" value={formatCurrency(summary.asp, 2)} detail="Revenue per unit" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MetricCard label="Stores selling" value={formatNumber(latestPoint?.storesSelling ?? null)} detail="Latest week" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MetricCard label="Store stock" value={formatNumber(latestPoint?.storeStock ?? null)} detail="Latest week" />
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MetricCard label="Sales cases" value={formatNumber(summary.salesCases, 2)} detail="Selected period" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MetricCard label="ASP" value={formatCurrency(summary.asp, 2)} detail="Revenue per unit" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MetricCard label="Stores ranged" value={formatNumber(latestPoint?.storesRanged ?? null)} detail="Latest day" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MetricCard label="Store stock" value={formatNumber(latestPoint?.storeStock ?? null)} detail="Latest day" />
                                </Grid>
                            </>
                        )}
                    </Grid>

                    <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", sm: "flex-end" }}
                            spacing={2}
                            sx={{ mb: 2 }}
                        >
                            <Box>
                                <Typography variant="h6">Performance Trend</Typography>
                            </Box>
                            <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={chartMode}
                                    onChange={handleChartModeChange}
                                    aria-label="Performance chart mode"
                                >
                                    <ToggleButton value="total">Total</ToggleButton>
                                    <ToggleButton value="sku">SKU</ToggleButton>
                                </ToggleButtonGroup>
                                {renderMetricSelect("Metric 1", metricOne, nextMetric => {
                                    setMetricOne(nextMetric);
                                    if (chartMode === "sku" && nextMetric !== "none") {
                                        void loadMetricDetails(nextMetric, true);
                                    }
                                })}
                                {renderMetricSelect("Metric 2", metricTwo, setMetricTwo, true, chartMode === "sku")}
                            </Stack>
                        </Stack>
                        {chartMode === "sku" && skuDetails?.isTruncated && (
                            <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
                                Showing up to {skuDetails.resultLimit ?? 100} mapped products ranked by the selected metric over this date range. Use product filters to narrow the results.
                            </Alert>
                        )}
                        {skuChartLoading ? (
                            <Box sx={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                <CircularProgress size={22} />
                                <Typography color="text.secondary">Loading SKU performance…</Typography>
                            </Box>
                        ) : skuDetailError ? (
                            <Alert severity="error">{skuDetailError}</Alert>
                        ) : chartMode === "sku" && chartSeries.length === 0 ? (
                            <Box sx={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography color="text.secondary">No SKU performance data available.</Typography>
                            </Box>
                        ) : (
                            <EposPerformanceTrendChart
                                periods={points.map(point => point.date)}
                                series={chartSeries}
                                colors={chartColors}
                                yAxis={chartYAxes}
                                leftAxis="metric-one"
                                rightAxis={chartMode === "total" && metricTwo !== "none" ? "metric-two" : null}
                            />
                        )}
                    </Card>

                    <Card variant="outlined" sx={{ mb: 3 }}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", sm: "center" }}
                            spacing={2}
                            sx={{
                                p: { xs: 2, sm: 2.5 },
                                pb: 2,
                                borderBottom: 1,
                                borderColor: "divider"
                            }}
                        >
                            <Typography variant="h6">
                                {grain === "daily" ? "Daily Performance" : "Weekly Performance"}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                <Tooltip title="Copy table to clipboard">
                                    <IconButton aria-label="Copy table to clipboard" onClick={copyPerformanceToClipboard} disabled={!isDateRangeQueryCurrent}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Export CSV">
                                    <IconButton aria-label="Export CSV" onClick={exportCsv} disabled={!isDateRangeQueryCurrent}>
                                        <FileDownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                        <TableContainer sx={{ overflowX: "auto" }}>
                            <Table
                                stickyHeader
                                size="small"
                                aria-label={grain === "daily" ? "daily performance table" : "weekly performance table"}
                                sx={{
                                    minWidth: 760,
                                    width: "max-content",
                                    tableLayout: "auto",
                                    "& .MuiTableBody-root .MuiTableCell-root": {
                                        whiteSpace: "nowrap"
                                    },
                                    "& .MuiTableHead-root .MuiTableCell-root": {
                                        whiteSpace: "nowrap"
                                    }
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            fontWeight: 700,
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 4,
                                            backgroundColor: theme => theme.palette.background.paper
                                        }}>
                                            Metric
                                        </TableCell>
                                        {points.map(point => (
                                            <TableCell key={point.date} align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                                {formatPeriod(point.date)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {metricDefinitions.map(metric => {
                                        const isExpanded = expandedMetrics.has(metric.key);
                                        const details = metricDetails[metric.key];
                                        const detailError = metricDetailErrors[metric.key];
                                        const detailLoading = metricDetailLoading[metric.key];

                                        return (
                                            <Fragment key={metric.key}>
                                                <TableRow>
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                        sx={{
                                                            fontWeight: 600,
                                                            whiteSpace: "nowrap",
                                                            position: "sticky",
                                                            left: 0,
                                                            zIndex: 1,
                                                            backgroundColor: theme => theme.palette.background.paper
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <IconButton
                                                                size="small"
                                                                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${metric.label}`}
                                                                aria-expanded={isExpanded}
                                                                onClick={() => toggleMetric(metric.key)}
                                                            >
                                                                {isExpanded
                                                                    ? <KeyboardArrowDownIcon fontSize="small" />
                                                                    : <KeyboardArrowRightIcon fontSize="small" />}
                                                            </IconButton>
                                                            {metric.label}
                                                        </Stack>
                                                    </TableCell>
                                                    {points.map(point => (
                                                        <TableCell key={point.date} align="right" sx={{ whiteSpace: "nowrap" }}>
                                                            {formatMetricValue(point, metric.key)}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                                {isExpanded && detailLoading && (
                                                    <TableRow>
                                                        <TableCell colSpan={points.length + 1}>
                                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 5 }}>
                                                                <CircularProgress size={18} />
                                                                <Typography color="text.secondary" variant="body2">
                                                                    Loading mapped products…
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {isExpanded && !detailLoading && detailError && (
                                                    <TableRow>
                                                        <TableCell colSpan={points.length + 1}>
                                                            <Alert severity="error">{detailError}</Alert>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {isExpanded && !detailLoading && !detailError && details?.isTruncated && (
                                                    <TableRow>
                                                        <TableCell colSpan={points.length + 1}>
                                                            <Alert severity="info">
                                                                Showing up to {details.resultLimit ?? 100} mapped products ranked by {metric.label.toLowerCase()} over this date range. Use product filters to narrow the results.
                                                            </Alert>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {isExpanded && !detailLoading && !detailError && details?.items.map(item => (
                                                    <TableRow key={`${metric.key}-${item.productId}-${item.productName}`}>
                                                        <TableCell
                                                            component="th"
                                                            scope="row"
                                                            sx={{
                                                                pl: 6,
                                                                whiteSpace: "nowrap",
                                                                position: "sticky",
                                                                left: 0,
                                                                zIndex: 1,
                                                                backgroundColor: theme => theme.palette.background.paper
                                                            }}
                                                        >
                                                            {item.productName}
                                                        </TableCell>
                                                        {points.map(point => (
                                                            <TableCell key={point.date} align="right" sx={{ whiteSpace: "nowrap" }}>
                                                                {formatMetricValueValue(
                                                                    item.values.find(value => value.period === point.date)?.value ?? null,
                                                                    metric.key)}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </>
            )}

            <Card variant="outlined">
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={2}
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        pb: 2,
                        borderBottom: 1,
                        borderColor: "divider"
                    }}
                >
                    <Typography variant="h6">Store Performance</Typography>
                    {!storePerformanceLoading && !storePerformanceError && storePerformanceAvailable && storePerformance.length > 0 && (
                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                            <Tooltip title="Copy table to clipboard">
                                <IconButton
                                    aria-label="Copy table to clipboard"
                                    onClick={copyStorePerformanceToClipboard}
                                    disabled={!isDateRangeQueryCurrent || storeCsvExportLoading}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={storeCsvExportLoading ? "Exporting CSV…" : "Export CSV"}>
                                <span>
                                    <IconButton
                                        aria-label="Export CSV"
                                        onClick={() => void exportStoreCsv()}
                                        disabled={!isDateRangeQueryCurrent || storeCsvExportLoading}
                                        aria-busy={storeCsvExportLoading}
                                    >
                                        {storeCsvExportLoading
                                            ? <CircularProgress size={20} />
                                            : <FileDownloadIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>
                {storeActionError && <Alert severity="error" sx={{ m: 2, mb: 0 }}>{storeActionError}</Alert>}
                {storePerformanceLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : storePerformanceError ? (
                    <Alert severity="error" sx={{ m: 2 }}>{storePerformanceError}</Alert>
                ) : !storePerformanceAvailable ? (
                    <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                        <Typography variant="h6">Store-level data is not available yet</Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            This table will appear after the first {grain} store-level import has completed.
                        </Typography>
                    </Box>
                ) : storePerformance.length === 0 ? (
                    <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                        <Typography variant="h6">No store-level data for these filters</Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            Try changing the date range or product filters.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ overflowX: "auto" }}>
                            <Table size="small" aria-label="store performance" sx={{ minWidth: 1500 }}>
                                <TableHead>
                                    <TableRow>
                                        {storeColumns.map(column => (
                                            <TableCell
                                                key={column.key}
                                                align={column.align ?? "left"}
                                                sortDirection={storeSortBy === column.key ? storeSortDirection : false}
                                                sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                                            >
                                                <TableSortLabel
                                                    active={storeSortBy === column.key}
                                                    direction={storeSortBy === column.key ? storeSortDirection : "asc"}
                                                    onClick={() => handleStoreSort(column.key)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {storePerformance.map(store => (
                                        <TableRow key={store.eposStoreId} hover>
                                            {storeColumns.map(column => {
                                                const value = store[column.key];
                                                return (
                                                    <TableCell key={column.key} align={column.align ?? "left"} sx={{ whiteSpace: "nowrap" }}>
                                                        {column.format ? column.format(value as number | null) : value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={storePerformanceTotal}
                            page={storePage}
                            rowsPerPage={storePageSize}
                            rowsPerPageOptions={[10, 25, 50]}
                            onPageChange={(_event, nextPage) => setStorePage(nextPage)}
                            onRowsPerPageChange={event => {
                                setStorePageSize(Number(event.target.value));
                                setStorePage(0);
                            }}
                        />
                    </>
                )}
            </Card>
            <EposStoreHeatmap
                retailer={selectedRetailer}
                fromDate={queryDateRange[0]?.format("YYYY-MM-DD") ?? null}
                toDate={queryDateRange[1]?.format("YYYY-MM-DD") ?? null}
                grain={grain}
                {...productFilterParams}
            />
        </Box>
    );
}

function MetricCard({ label, value, detail }: { label: string; value: string; detail: string }) {
    return (
        <Card variant="outlined" sx={{ height: "100%", p: 2 }}>
            <Typography color="text.secondary" variant="body2">{label}</Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>{value}</Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>{detail}</Typography>
        </Card>
    );
}
